import { getMapLabelIndexSummary, loadMapLabelTuples } from "./android-map-label-index";
import type { GazetteerTuple } from "./place-search-core";

export type PeakElevationAuditItem = {
  id: string;
  name: string;
  kind: string;
  latitude: number;
  longitude: number;
  elevationM: number | null;
  elevationSource: string | null;
  elevationStatus: string | null;
  packIds: string[];
};

export type PeakElevationAuditPack = {
  mapPackId: string;
  mountains: number;
  withElevation: number;
  withoutElevation: number;
  coveragePercent: number;
};

export type PeakElevationAuditReport = {
  schema: "THERA_PEAK_ELEVATION_AUDIT_V1";
  generatedAt: string;
  expectedIndexVersion: 3;
  index: {
    building: boolean;
    readyCount: number;
    indexedCount: number;
    progress: number;
    mapPackIds: string[];
  };
  totals: {
    tuples: number;
    mountains: number;
    namedMountains: number;
    withElevation: number;
    withoutElevation: number;
    invalidElevation: number;
    coveragePercent: number;
  };
  elevationSources: Record<string, number>;
  packs: PeakElevationAuditPack[];
  withElevation: PeakElevationAuditItem[];
  withoutElevation: PeakElevationAuditItem[];
};

function normalizedKind(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es")
    .trim();
}

export function isPeakElevationKind(value: unknown) {
  const kind = normalizedKind(value);
  return [
    "cerro/montana",
    "cerro",
    "montana",
    "mountain",
    "peak",
    "summit",
    "volcano",
    "volcan",
    "hill",
  ].includes(kind);
}

function parsedElevation(tuple: GazetteerTuple) {
  const raw: unknown = tuple[8];
  if (raw === null || raw === undefined || raw === "") {
    return { value: null, invalid: false };
  }
  const value = Number(raw);
  if (!Number.isFinite(value) || value < -500 || value > 9_000) {
    return { value: null, invalid: true };
  }
  return { value, invalid: false };
}

function tupleToAuditItem(tuple: GazetteerTuple, elevationM: number | null): PeakElevationAuditItem {
  return {
    id: String(tuple[0] ?? ""),
    name: String(tuple[1] ?? "").trim(),
    kind: String(tuple[5] ?? ""),
    latitude: Number(tuple[3]),
    longitude: Number(tuple[4]),
    elevationM,
    elevationSource: typeof tuple[9] === "string" && tuple[9].trim() ? tuple[9].trim() : null,
    elevationStatus: typeof tuple[10] === "string" && tuple[10].trim() ? tuple[10].trim() : null,
    packIds: Array.isArray(tuple[7]) ? [...tuple[7]] : [],
  };
}

function coveragePercent(withElevation: number, mountains: number) {
  return mountains > 0 ? Math.round((withElevation / mountains) * 10_000) / 100 : 0;
}

export function buildPeakElevationAudit(
  tuples: GazetteerTuple[],
  index: PeakElevationAuditReport["index"],
  generatedAt = new Date().toISOString(),
): PeakElevationAuditReport {
  const withElevation: PeakElevationAuditItem[] = [];
  const withoutElevation: PeakElevationAuditItem[] = [];
  const sourceCounts: Record<string, number> = {};
  const packCounters = new Map<string, { mountains: number; withElevation: number; withoutElevation: number }>();
  let namedMountains = 0;
  let invalidElevation = 0;

  for (const tuple of tuples) {
    if (!Array.isArray(tuple) || tuple.length < 8 || !isPeakElevationKind(tuple[5])) continue;
    const parsed = parsedElevation(tuple);
    if (parsed.invalid) invalidElevation += 1;
    const item = tupleToAuditItem(tuple, parsed.value);
    if (item.name) namedMountains += 1;

    if (parsed.value === null) {
      withoutElevation.push(item);
    } else {
      withElevation.push(item);
      const source = item.elevationSource ?? "metadato-sin-fuente";
      sourceCounts[source] = (sourceCounts[source] ?? 0) + 1;
    }

    for (const mapPackId of item.packIds) {
      const current = packCounters.get(mapPackId) ?? { mountains: 0, withElevation: 0, withoutElevation: 0 };
      current.mountains += 1;
      if (parsed.value === null) current.withoutElevation += 1;
      else current.withElevation += 1;
      packCounters.set(mapPackId, current);
    }
  }

  const byName = (left: PeakElevationAuditItem, right: PeakElevationAuditItem) => left.name.localeCompare(right.name, "es");
  withElevation.sort(byName);
  withoutElevation.sort(byName);

  const mountains = withElevation.length + withoutElevation.length;
  const packs = [...packCounters.entries()]
    .map(([mapPackId, value]) => ({
      mapPackId,
      ...value,
      coveragePercent: coveragePercent(value.withElevation, value.mountains),
    }))
    .sort((left, right) => right.mountains - left.mountains || left.mapPackId.localeCompare(right.mapPackId, "es"));

  return {
    schema: "THERA_PEAK_ELEVATION_AUDIT_V1",
    generatedAt,
    expectedIndexVersion: 3,
    index: {
      ...index,
      mapPackIds: [...index.mapPackIds].sort((left, right) => left.localeCompare(right, "es")),
    },
    totals: {
      tuples: tuples.length,
      mountains,
      namedMountains,
      withElevation: withElevation.length,
      withoutElevation: withoutElevation.length,
      invalidElevation,
      coveragePercent: coveragePercent(withElevation.length, mountains),
    },
    elevationSources: Object.fromEntries(Object.entries(sourceCounts).sort((left, right) => right[1] - left[1])),
    packs,
    withElevation,
    withoutElevation,
  };
}

export async function auditPeakElevationIndex(): Promise<PeakElevationAuditReport> {
  const [summary, tuples] = await Promise.all([
    getMapLabelIndexSummary(),
    loadMapLabelTuples(),
  ]);
  return buildPeakElevationAudit(tuples, {
    building: summary.building,
    readyCount: summary.readyCount,
    indexedCount: summary.indexedCount,
    progress: summary.progress,
    mapPackIds: summary.mapPackIds,
  });
}
